import './style.scss';

import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { LocationTrackingState, TrackingStatus } from '../../../hooks/useLocationTracking';
import LocationMap from '../location-map';

interface LocationTrackerProps {
	variant?: 'header' | 'fixed';
	className?: string;
	tracking: LocationTrackingState;
}

const STATUS_CONFIG: Record<
	TrackingStatus,
	{
		label: string;
		ariaLabel: string;
	}
> = {
	'idle': { label: 'Off', ariaLabel: 'Location tracking is not running' },
	'needs-permission': { label: 'Allow location', ariaLabel: 'Location permission needed' },
	'initializing': { label: 'Locating…', ariaLabel: 'Acquiring your location' },
	'active': { label: 'Tracking', ariaLabel: 'Location tracking active' },
	'updating': { label: 'Updating…', ariaLabel: 'Updating your location' },
	'paused': { label: 'Paused', ariaLabel: 'Location tracking paused while the app is in the background' },
	'send-failed': { label: 'Not sent', ariaLabel: 'Your location was found but not sent. It will retry.' },
	'error': { label: 'No GPS fix', ariaLabel: 'Your location could not be found' },
	'permission-denied': { label: 'Location off', ariaLabel: 'Location permission is blocked' },
};

/** 6.5244 → "6.5244° N"; the sign picks the hemisphere instead of assuming Nigeria's. */
export const formatCoordinate = (value: number, axis: 'lat' | 'lng', digits = 6) => {
	const hemisphere = axis === 'lat' ? (value < 0 ? 'S' : 'N') : value < 0 ? 'W' : 'E';
	return `${Math.abs(value).toFixed(digits)}° ${hemisphere}`;
};

/** What the panel says, and offers, when there is no fix to show. */
const NO_FIX_COPY: Partial<Record<TrackingStatus, { text: string; action?: string }>> = {
	'needs-permission': {
		text: 'Your browser is asking to use your location. Choose Allow so your desk can see you are on the ground.',
		action: 'Ask again',
	},
	'initializing': { text: 'Finding your location. This can take up to 15 seconds outdoors.', action: 'Try again' },
	'permission-denied': {
		text: 'Location is blocked for this site. Turn it on in your browser or phone settings, then try again.',
		action: 'Try again',
	},
	'error': { text: '', action: 'Try again' },
	'paused': { text: 'Paused while the app is in the background. It resumes when you come back.' },
};

const getRelativeTime = (isoString: string): string => {
	const diff = Date.now() - new Date(isoString).getTime();
	const seconds = Math.floor(diff / 1000);
	if (seconds < 60) return 'Just now';
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes} min ago`;
	const hours = Math.floor(minutes / 60);
	return `${hours}h ago`;
};

const LocationTracker: React.FC<LocationTrackerProps> = ({ variant = 'header', className = '', tracking }) => {
	const { latitude, longitude, accuracy, error, lastUpdated, status, retry } = tracking;

	const [isExpanded, setIsExpanded] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const config = STATUS_CONFIG[status];

	const handleClickOutside = useCallback((e: MouseEvent) => {
		if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
			setIsExpanded(false);
		}
	}, []);

	useEffect(() => {
		if (isExpanded) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [isExpanded, handleClickOutside]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isExpanded) {
				setIsExpanded(false);
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isExpanded]);

	const ariaDescription = useMemo(() => {
		let desc = config.ariaLabel;
		if (latitude !== null && longitude !== null) {
			desc += `. Coordinates: ${formatCoordinate(latitude, 'lat', 4)}, ${formatCoordinate(longitude, 'lng', 4)}`;
		}
		if (lastUpdated) {
			desc += `. Last updated ${getRelativeTime(lastUpdated)}`;
		}
		return desc;
	}, [config.ariaLabel, latitude, longitude, lastUpdated]);

	const hasCoords = latitude !== null && longitude !== null;

	return (
		<div
			className={`location-tracker location-tracker--${variant} ${className}`.trim()}
			ref={containerRef}
		>
			<button
				type='button'
				className={`location-tracker__pill location-tracker__pill--${status}`}
				onClick={() => setIsExpanded(!isExpanded)}
				aria-live='polite'
				aria-label={ariaDescription}
				aria-expanded={isExpanded}
				aria-controls='location-tracker-panel'
			>
				<span className={`location-tracker__dot location-tracker__dot--${status}`} />
				<span className={`location-tracker__label location-tracker__label--${status}`}>{config.label}</span>
				{hasCoords && variant === 'header' && (
					<span className='location-tracker__coords'>
						{latitude.toFixed(4)}, {longitude.toFixed(4)}
					</span>
				)}
				<svg
					className={`location-tracker__chevron ${isExpanded ? 'location-tracker__chevron--open' : ''}`}
					width='10'
					height='10'
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='2.5'
					strokeLinecap='round'
					strokeLinejoin='round'
				>
					<polyline points='6 9 12 15 18 9' />
				</svg>
			</button>

			{isExpanded && (
				<div
					id='location-tracker-panel'
					className='location-tracker__panel'
					role='dialog'
					aria-label='Location tracking details'
				>
					<div className='location-tracker__panel-header'>
						<h4>Your location</h4>
						<button
							type='button'
							className='location-tracker__panel-close'
							onClick={() => setIsExpanded(false)}
							aria-label='Close location details'
						>
							<svg
								width='14'
								height='14'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
							>
								<line
									x1='18'
									y1='6'
									x2='6'
									y2='18'
								/>
								<line
									x1='6'
									y1='6'
									x2='18'
									y2='18'
								/>
							</svg>
						</button>
					</div>
					<div className='location-tracker__panel-body'>
						<div className='location-tracker__status-row'>
							<span className={`location-tracker__dot location-tracker__dot--${status}`} />
							<span className={`location-tracker__label location-tracker__label--${status}`}>{config.label}</span>
						</div>

						{hasCoords ? (
							<>
								<div className='location-tracker__details'>
									<div className='location-tracker__detail-row'>
										<span className='location-tracker__detail-row-label'>Latitude</span>
										<span className='location-tracker__detail-row-value'>{formatCoordinate(latitude, 'lat')}</span>
									</div>
									<div className='location-tracker__detail-row'>
										<span className='location-tracker__detail-row-label'>Longitude</span>
										<span className='location-tracker__detail-row-value'>{formatCoordinate(longitude, 'lng')}</span>
									</div>
									{accuracy !== null && (
										<div className='location-tracker__detail-row'>
											<span className='location-tracker__detail-row-label'>Accuracy</span>
											<span className='location-tracker__detail-row-value'>&plusmn;{Math.round(accuracy)}m</span>
										</div>
									)}
								</div>
								<div className='location-tracker__panel-map'>
									<LocationMap
										latitude={latitude}
										longitude={longitude}
										height='180px'
									/>
								</div>
							</>
						) : (
							<div className='location-tracker__panel-error'>
								<p>{NO_FIX_COPY[status]?.text || error || 'Your location could not be found.'}</p>
								{NO_FIX_COPY[status]?.action ? (
									<button
										type='button'
										className='location-tracker__retry-btn'
										onClick={retry}
									>
										{NO_FIX_COPY[status]?.action}
									</button>
								) : null}
							</div>
						)}

						{status === 'send-failed' ? (
							<p className='location-tracker__send-failed'>
								Found you, but the last update did not reach your desk. It retries automatically.{' '}
								<button
									type='button'
									className='location-tracker__retry-btn'
									onClick={retry}
								>
									Send now
								</button>
							</p>
						) : null}

						{lastUpdated && <div className='location-tracker__timestamp'>Last updated: {getRelativeTime(lastUpdated)}</div>}
					</div>
				</div>
			)}
		</div>
	);
};

export default LocationTracker;
